// Admin section — rendered at the top of Dashboard for the owner.
// v2: no global platform fee or min swap — they're per-pair. Each pair row has
// editors for fee/min/public/active + a Remove button (only when no pools).

import { useEffect, useState } from "react";
import type { AppConfig } from "../config";
import * as E from "../escrow";
import { tokenTicker } from "../ui";

const MAX_PLATFORM_FEE_BPS = 500; // mirror of contract constant

export default function AdminSection({
  cfg,
  address,
  pairs,
  onDone,
}: {
  cfg: AppConfig;
  address: string;
  pairs: E.EscrowPair[];
  onDone: () => void;
}) {
  return (
    <div className="card" style={{ marginBottom: "1rem", border: "1px solid var(--accent)" }}>
      <div className="row" style={{ marginBottom: "0.6rem" }}>
        <h3 style={{ margin: 0 }}>Admin</h3>
        <span className="badge">owner</span>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
        <AddPairForm cfg={cfg} address={address} onDone={onDone} />
        <FeeCollectorForm cfg={cfg} address={address} onDone={onDone} />
      </div>

      <div style={{ marginTop: "1rem" }}>
        <h4 style={{ marginBottom: "0.5rem" }}>Existing pairs</h4>
        {pairs.length === 0 ? (
          <p className="muted">No pairs yet — add one above.</p>
        ) : (
          <div className="col" style={{ gap: "0.5rem" }}>
            {pairs.map((p) => (
              <PairRow key={p.id} cfg={cfg} address={address} pair={p} onDone={onDone} />
            ))}
          </div>
        )}
      </div>

      <EmergencySection cfg={cfg} address={address} onDone={onDone} />
    </div>
  );
}

// ── Add pair ──────────────────────────────────────────────────────────
function AddPairForm({
  cfg,
  address,
  onDone,
}: {
  cfg: AppConfig;
  address: string;
  onDone: () => void;
}) {
  const [staked, setStaked] = useState("");
  const [free, setFree] = useState("");
  const [pub, setPub] = useState(true);
  const [feeStr, setFeeStr] = useState("1");
  const [minStr, setMinStr] = useState("100");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hash, setHash] = useState<string | null>(null);

  const feeBps = Math.round(Number(feeStr || "0") * 100);
  const feeCapHit = feeBps > MAX_PLATFORM_FEE_BPS;
  const minWei = minStr ? E.parseAmount(minStr) : 0n;
  const valid = staked && free && staked !== free && !feeCapHit && minStr && !busy;

  async function onSubmit() {
    setErr(null);
    setHash(null);
    setBusy(true);
    try {
      const tx = await E.buildAddPair(cfg, address, staked, free, pub, feeBps, minWei);
      const h = await E.signAndSend(cfg, tx);
      setHash(h);
      await E.awaitCompleted(cfg, h);
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="col">
      <h4 style={{ margin: 0 }}>Add a pair</h4>
      <label className="muted">Staked token (Meta-ESDT collection id)</label>
      <input value={staked} onChange={(e) => setStaked(e.target.value)} placeholder="SFOXSY-xxxxxx" />
      <label className="muted">Free token (fungible)</label>
      <input value={free} onChange={(e) => setFree(e.target.value)} placeholder="FOXSY-xxxxxx" />
      <div className="row" style={{ gap: "0.8rem" }}>
        <div style={{ flex: 1 }}>
          <label className="muted">Platform fee % (cap 5%)</label>
          <input value={feeStr} onChange={(e) => setFeeStr(e.target.value)} />
          {feeCapHit && (
            <p style={{ color: "var(--danger)", fontSize: "0.8rem", margin: 0 }}>exceeds 5% hard cap</p>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <label className="muted">Min swap amount</label>
          <input value={minStr} onChange={(e) => setMinStr(e.target.value)} />
        </div>
      </div>
      <label className="row" style={{ gap: "0.4rem" }}>
        <input
          type="checkbox"
          checked={pub}
          onChange={(e) => setPub(e.target.checked)}
          style={{ width: "auto" }}
        />
        <span>Anyone can create pools (public)</span>
      </label>
      <button onClick={onSubmit} disabled={!valid}>
        {busy ? "Submitting…" : "Add pair"}
      </button>
      {err && <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>}
      {hash && <TxLink cfg={cfg} hash={hash} />}
    </div>
  );
}

// ── Fee collector ─────────────────────────────────────────────────────
function FeeCollectorForm({
  cfg,
  address,
  onDone,
}: {
  cfg: AppConfig;
  address: string;
  onDone: () => void;
}) {
  const [collectorStr, setCollectorStr] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hash, setHash] = useState<string | null>(null);

  async function onSubmit() {
    setErr(null);
    setHash(null);
    setBusy(true);
    try {
      const tx = await E.buildSetFeeCollector(cfg, address, collectorStr);
      const h = await E.signAndSend(cfg, tx);
      setHash(h);
      await E.awaitCompleted(cfg, h);
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="col">
      <h4 style={{ margin: 0 }}>Fee collector</h4>
      <p className="muted" style={{ fontSize: "0.85rem", margin: 0 }}>
        Where the platform fee from each swap is sent. Defaults to the contract
        owner if unset.
      </p>
      <label className="muted">Collector address</label>
      <div className="row">
        <input
          value={collectorStr}
          onChange={(e) => setCollectorStr(e.target.value)}
          placeholder="erd1…"
        />
        <button onClick={onSubmit} disabled={!collectorStr || busy}>
          {busy ? "…" : "Set"}
        </button>
      </div>
      {err && <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>}
      {hash && <TxLink cfg={cfg} hash={hash} />}
    </div>
  );
}

// ── Per-pair config row ───────────────────────────────────────────────
function PairRow({
  cfg,
  address,
  pair,
  onDone,
}: {
  cfg: AppConfig;
  address: string;
  pair: E.EscrowPair;
  onDone: () => void;
}) {
  const [feeStr, setFeeStr] = useState((pair.platformFeeBps / 100).toString());
  const [minStr, setMinStr] = useState(E.fmtAmount(pair.minSwapAmount));
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [hash, setHash] = useState<string | null>(null);

  const newFeeBps = Math.round(Number(feeStr || "0") * 100);
  const feeCapHit = newFeeBps > MAX_PLATFORM_FEE_BPS;
  const feeChanged = newFeeBps !== pair.platformFeeBps;
  const minWei = minStr ? E.parseAmount(minStr) : 0n;
  const minChanged = minWei !== pair.minSwapAmount;

  async function run(label: string, build: () => Promise<any>) {
    setErr(null);
    setHash(null);
    setBusy(label);
    try {
      const tx = await build();
      const h = await E.signAndSend(cfg, tx);
      setHash(h);
      await E.awaitCompleted(cfg, h);
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      className="card"
      style={{
        padding: "0.8rem 1rem",
        background: "var(--bg-elev-2)",
        border: "1px solid var(--border)",
      }}
    >
      <div className="row" style={{ marginBottom: "0.4rem", flexWrap: "wrap" }}>
        <strong>Pair #{pair.id}</strong>
        <span className="mono muted">
          {pair.stakedToken} → {pair.freeToken}
        </span>
        <span className={`badge ${pair.publicPools ? "live" : ""}`}>
          {pair.publicPools ? "public" : "private"}
        </span>
        <span className={`badge ${pair.active ? "live" : "test"}`}>
          {pair.active ? "active" : "paused"}
        </span>
        <div style={{ flex: 1 }} />
        <button
          className="ghost"
          disabled={!!busy}
          onClick={() =>
            run("public", () =>
              E.buildSetPairPublic(cfg, address, pair.id, !pair.publicPools),
            )
          }
        >
          {busy === "public" ? "…" : pair.publicPools ? "Make private" : "Make public"}
        </button>
        <button
          className="ghost"
          disabled={!!busy}
          onClick={() =>
            run("active", () =>
              E.buildSetPairActive(cfg, address, pair.id, !pair.active),
            )
          }
          title={
            pair.active
              ? "Pause swaps on this pair (creators can still exit)"
              : "Resume swaps on this pair"
          }
        >
          {busy === "active" ? "…" : pair.active ? "Pause swaps" : "Resume swaps"}
        </button>
        <button
          className="ghost"
          disabled={!!busy}
          onClick={() => {
            if (
              confirm(
                "Remove this pair? Only allowed when no pools exist on it. " +
                  "Close or force-close all pools first.",
              )
            ) {
              run("remove", () => E.buildRemovePair(cfg, address, pair.id));
            }
          }}
          style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
        >
          {busy === "remove" ? "…" : "Remove pair"}
        </button>
      </div>

      <div className="row" style={{ gap: "0.8rem", flexWrap: "wrap", marginTop: "0.3rem" }}>
        <div className="row" style={{ gap: "0.3rem" }}>
          <label className="muted">Default fee %</label>
          <input
            value={feeStr}
            onChange={(e) => setFeeStr(e.target.value)}
            style={{ width: "5rem" }}
          />
          <button
            disabled={!!busy || !feeChanged || feeCapHit}
            onClick={() =>
              run("fee", () =>
                E.buildSetPairPlatformFee(cfg, address, pair.id, newFeeBps),
              )
            }
          >
            {busy === "fee" ? "…" : "Save"}
          </button>
          {feeCapHit && (
            <span style={{ color: "var(--danger)", fontSize: "0.8rem" }}>over 5%</span>
          )}
        </div>
        <div className="row" style={{ gap: "0.3rem" }}>
          <label className="muted">Default min swap</label>
          <input
            value={minStr}
            onChange={(e) => setMinStr(e.target.value)}
            style={{ width: "7rem" }}
          />
          <button
            disabled={!!busy || !minChanged}
            onClick={() =>
              run("min", () =>
                E.buildSetPairMinSwap(cfg, address, pair.id, minWei),
              )
            }
          >
            {busy === "min" ? "…" : "Save"}
          </button>
        </div>
        <span className="muted" style={{ fontSize: "0.8rem" }}>
          (defaults for NEW pools — existing pools keep their snapshot)
        </span>
      </div>

      {err && <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>}
      {hash && <TxLink cfg={cfg} hash={hash} />}
    </div>
  );
}

// ── Emergency Withdraw ───────────────────────────────────────────────
// Nuclear button. Drains every selected token+nonce held by the contract
// to the owner and wipes pool state across all pairs. Pair config stays.
// We pull holdings off the API so the owner sees exactly what they're
// about to take — no blind argument-typing.
function EmergencySection({
  cfg,
  address,
  onDone,
}: {
  cfg: AppConfig;
  address: string;
  onDone: () => void;
}) {
  const [holdings, setHoldings] = useState<E.Holding[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hash, setHash] = useState<string | null>(null);

  const key = (h: E.Holding) => `${h.token}|${h.nonce}`;

  async function refresh() {
    setRefreshing(true);
    setErr(null);
    try {
      const list = await E.getContractHoldings(cfg);
      setHoldings(list);
      // Default: everything selected — emergency means "get it all out".
      setSelected(new Set(list.map(key)));
    } catch (e) {
      setErr(String(e));
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => { refresh(); }, [cfg.contractAddress]);

  function toggle(h: E.Holding) {
    const k = key(h);
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  }

  async function execute() {
    if (!holdings) return;
    const picks = holdings.filter((h) => selected.has(key(h)));
    if (picks.length === 0) {
      setErr("Select at least one token to withdraw.");
      return;
    }
    setErr(null);
    setHash(null);
    setBusy(true);
    try {
      const tx = await E.buildEmergencyWithdraw(cfg, address, picks);
      const h = await E.signAndSend(cfg, tx);
      setHash(h);
      await E.awaitCompleted(cfg, h);
      setArmed(false);
      await refresh();
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="card"
      style={{
        marginTop: "1.5rem",
        padding: "0.9rem 1rem",
        background: "rgba(255, 70, 70, 0.06)",
        border: "1px solid var(--danger)",
      }}
    >
      <div className="row" style={{ marginBottom: "0.4rem", flexWrap: "wrap" }}>
        <h4 style={{ margin: 0, color: "var(--danger)" }}>⚠ Emergency Withdraw</h4>
        <span className="muted" style={{ fontSize: "0.85rem" }}>
          last-resort: drain selected tokens to the owner & wipe all pool state
        </span>
        <div style={{ flex: 1 }} />
        <button className="ghost" disabled={refreshing} onClick={refresh}>
          {refreshing ? "…" : "Refresh"}
        </button>
      </div>

      <p className="muted" style={{ fontSize: "0.85rem", margin: "0 0 0.5rem 0" }}>
        Sends every selected (token, nonce, balance) the contract holds to the owner
        wallet, then clears every pool across every pair. Pair configurations are
        preserved. Use only if a pool is stuck or the contract needs to be wound
        down — normal exits should go through <strong>closePool</strong> /
        <strong> forceClosePool</strong>.
      </p>

      {holdings === null ? (
        <p className="muted">loading holdings…</p>
      ) : holdings.length === 0 ? (
        <p className="muted">Contract holds no tokens — nothing to withdraw.</p>
      ) : (
        <div className="col" style={{ gap: "0.25rem", marginBottom: "0.5rem" }}>
          {holdings.map((h) => (
            <label
              key={key(h)}
              className="row mono"
              style={{ gap: "0.5rem", fontSize: "0.85rem" }}
            >
              <input
                type="checkbox"
                checked={selected.has(key(h))}
                onChange={() => toggle(h)}
                style={{ width: "auto" }}
                disabled={busy}
              />
              <span style={{ minWidth: "12rem" }}>
                {tokenTicker(h.token)}
                <span className="muted"> ({h.token})</span>
              </span>
              <span className="muted">nonce {h.nonce}</span>
              <span style={{ flex: 1 }} />
              <span>{E.fmtAmount(h.balance)}</span>
            </label>
          ))}
        </div>
      )}

      {holdings && holdings.length > 0 && (
        <div className="row" style={{ gap: "0.6rem", flexWrap: "wrap" }}>
          {!armed ? (
            <button
              onClick={() => setArmed(true)}
              disabled={busy || selected.size === 0}
              style={{
                background: "var(--danger)",
                color: "white",
                border: "1px solid var(--danger)",
              }}
            >
              Arm emergency withdraw…
            </button>
          ) : (
            <>
              <span style={{ color: "var(--danger)" }}>
                Confirm: withdraw {selected.size} token entr{selected.size === 1 ? "y" : "ies"} & wipe all pools?
              </span>
              <button
                onClick={execute}
                disabled={busy}
                style={{
                  background: "var(--danger)",
                  color: "white",
                  border: "1px solid var(--danger)",
                }}
              >
                {busy ? "Submitting…" : "Yes, withdraw & wipe"}
              </button>
              <button className="ghost" onClick={() => setArmed(false)} disabled={busy}>
                Cancel
              </button>
            </>
          )}
        </div>
      )}

      {err && <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>}
      {hash && <TxLink cfg={cfg} hash={hash} />}
    </div>
  );
}

function TxLink({ cfg, hash }: { cfg: AppConfig; hash: string }) {
  const sub = cfg.network === "mainnet" ? "" : `${cfg.network}-`;
  return (
    <p className="mono muted" style={{ fontSize: "0.85rem" }}>
      tx:{" "}
      <a href={`https://${sub}explorer.multiversx.com/transactions/${hash}`} target="_blank" rel="noreferrer">
        {hash.slice(0, 14)}…
      </a>
    </p>
  );
}
